import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, LogIn } from 'lucide-react';
import { getEndpointUrl } from '../config/api';
import { getTokenTimeRemaining, isTokenExpired } from '../utils/auth';

const Login = ({ setUser, isModal = false }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isAdminLogin, setIsAdminLogin] = useState(false);  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    return newErrors;
  };  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('=== LOGIN ATTEMPT ===');
    console.log('Form data:', formData);
    console.log('Is admin login:', isAdminLogin);
    
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }try {      // Use different endpoints for admin vs regular login
      const loginEndpoint = isAdminLogin 
        ? getEndpointUrl('AUTH', 'ADMIN_LOGIN')
        : getEndpointUrl('AUTH', 'LOGIN');
      
      const response = await fetch(loginEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });      if (response.ok) {
        const data = await response.json();
        console.log('=== LOGIN SUCCESS ===');
        console.log('Full response data:', data);
        console.log('Token:', data.token);
        console.log('User:', data.user);
        
        // Store the JWT token
        if (data.token) {
          localStorage.setItem('token', data.token);
          console.log('Token stored in localStorage');
        } else {
          console.error('No token in response!');
        }
          // Create user object from response
        if (data.user) {
          const rawRole = data.user.role || '';
          const normalizedRole = String(rawRole).trim().toUpperCase().replace('ROLE_', '');
          const user = {
            id: data.user.id ?? data.user.userId ?? null,
            name: data.user.name,
            email: data.user.email,
            role: normalizedRole === 'ADMIN' ? 'admin' : 'user'
          };
          
          console.log('Created user object:', user);
          console.log('User role (should be lowercase):', user.role);
          console.log('Backend sent role:', data.user.role);
          
          setUser(user);
          localStorage.setItem('user', JSON.stringify(user));          // Verify token was stored
          const storedToken = localStorage.getItem('token');
          const storedUser = localStorage.getItem('user');
          console.log('Token verification - stored token:', storedToken ? 'Present' : 'Missing');
          console.log('User verification - stored user:', storedUser ? 'Present' : 'Missing');
            // Immediate token expiration check
          if (storedToken) {
            const timeRemaining = getTokenTimeRemaining(storedToken);
            const expired = isTokenExpired(storedToken, user); // Pass user object to help identify admin
            console.log('🚨 IMMEDIATE TOKEN CHECK AFTER STORAGE:');
            console.log('Time remaining (ms):', timeRemaining);
            console.log('Time remaining (minutes):', Math.round(timeRemaining / 1000 / 60));
            console.log('Token expired:', expired);
            
            if (expired) {
              console.error('❌ TOKEN IS EXPIRED IMMEDIATELY AFTER LOGIN! This should not happen.');
            } else {
              console.log('✅ Token is valid after login');
            }
          }
          
          // Navigate or call setUser based on modal mode
          if (isModal) {
            // In modal mode, just call setUser - parent will handle navigation
            console.log('Modal mode - not navigating');
          } else {
            const targetUrl = user.role === 'admin' ? '/admin' : '/';
            console.log('Navigating to:', targetUrl);
            console.log('User role check:', user.role, '=== admin?', user.role === 'admin');
            navigate(targetUrl);
          }
        } else {
          console.error('No user data in response!');
          setErrors({ password: 'Invalid response from server' });
        }      } else if (response.status === 401) {
        console.log('401 Unauthorized');
        setErrors({ password: 'Invalid email or password' });
      } else {
        console.log('Other error:', response.status);
        const errorText = await response.text();
        console.log('Error response:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          setErrors({ password: errorData.message || 'Login failed' });
        } catch {
          setErrors({ password: 'Login failed' });
        }
      }
    } catch (error) {
      console.error('=== LOGIN ERROR ===');
      console.error('Error details:', error);
      setErrors({ password: 'Unable to connect to server. Please try again.' });
    }
  };
  return (
    <div className={isModal ? "auth-modal-content" : "auth-container"}>
      <div className={isModal ? "auth-modal-form" : "auth-card"}>
        {!isModal && (
          <div className="auth-header">
            <LogIn className="auth-icon" size={32} />
            <h2>{isAdminLogin ? 'Admin Login' : 'Welcome Back'}</h2>
            <p>{isAdminLogin ? 'Access admin panel' : 'Sign in to your account'}</p>
          </div>
        )}
        
        {!isModal && (
          <div className="login-toggle">
            <button 
              className={!isAdminLogin ? 'active' : ''}
              onClick={() => setIsAdminLogin(false)}
            >
              User Login
            </button>
            <button 
              className={isAdminLogin ? 'active' : ''}
              onClick={() => setIsAdminLogin(true)}
            >
              Admin Login
            </button>
          </div>
        )}        {!isModal && isAdminLogin && (
          <div className="admin-info">
            <p><strong>Admin Access:</strong></p>
            <p>Please use your admin credentials to access the admin panel.</p>
          </div>        )}
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-wrapper">
              <Mail size={20} className="input-icon" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={isAdminLogin ? "Enter admin email" : "your.email@example.com"}
                className={errors.email ? 'error' : ''}
              />
            </div>
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <Lock size={20} className="input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={isAdminLogin ? "Enter admin password" : "Enter your secure password"}
                className={errors.password ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <button type="submit" className="auth-btn">
            {isAdminLogin ? 'Admin Login' : 'Sign In'}
          </button>
        </form>

        {!isModal && !isAdminLogin && (
          <div className="auth-footer">
            <p>Don't have an account? <Link to="/register">Create one</Link></p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
